/**
 * Converts bag products to a PreviewOrderRequest for the checkout API
 * @param bagProducts - Array of products from the bag store
 * @param paymentMethodId - Selected payment method ID
 * @param creatorAttributionToken - Creator attribution token for tracking creator influence
 * @returns PreviewOrderRequest object
 * @throws Error if bagProducts is empty, null, or contains more than one product
 */
export const createPreviewOrderRequest = (bagProducts, paymentMethodId, creatorAttributionToken) => {
    if (!bagProducts || bagProducts.length === 0) {
        throw new Error('Cannot preview order: bag is empty.');
    }
    else if (bagProducts.length > 1) {
        throw new Error('Cannot preview order: bag contains more than one product.');
    }
    const bagProduct = bagProducts[0];
    const product = {
        products: [
            {
                asin: bagProduct.asin,
                quantity: bagProduct.quantity,
                offerDetails: {
                    offerId: bagProduct.offerId,
                    merchantId: bagProduct.merchantId,
                    selectedOfferId: bagProduct.selectedOfferId,
                },
            },
        ],
    };
    if (paymentMethodId) {
        product.paymentMethodId = paymentMethodId;
    }
    if (creatorAttributionToken) {
        product.creatorAttributionToken = creatorAttributionToken;
    }
    return product;
};
export var PreviewOrderStatus;
(function (PreviewOrderStatus) {
    // API errors or responses containing an "errorReason" either without an error code or with non-fixable codes.
    PreviewOrderStatus[PreviewOrderStatus["UNFIXABLE_ERROR"] = 0] = "UNFIXABLE_ERROR";
    // API response containing an "errorReason" with a "fixable" error code.
    // An error is fixable if it's related to invalid payment method or delivery address.
    PreviewOrderStatus[PreviewOrderStatus["FIXABLE_ERROR"] = 1] = "FIXABLE_ERROR";
    PreviewOrderStatus[PreviewOrderStatus["SUCCESS"] = 2] = "SUCCESS";
})(PreviewOrderStatus || (PreviewOrderStatus = {}));
export var PreviewOrderErrorType;
(function (PreviewOrderErrorType) {
    PreviewOrderErrorType[PreviewOrderErrorType["PAYMENT_METHOD"] = 0] = "PAYMENT_METHOD";
    PreviewOrderErrorType[PreviewOrderErrorType["DELIVERY_ADDRESS"] = 1] = "DELIVERY_ADDRESS";
    PreviewOrderErrorType[PreviewOrderErrorType["UNKNOWN"] = 2] = "UNKNOWN";
    PreviewOrderErrorType[PreviewOrderErrorType["NONE"] = 3] = "NONE";
})(PreviewOrderErrorType || (PreviewOrderErrorType = {}));
export const PaymentMethodErrorMessage = {
    DEFAULT_PAYMENT_METHOD_NOT_SET: 'defaultPaymentMethodNotSet',
    EXPIRED_DEFAULT_PAYMENT_INSTRUMENT: 'expiredDefaultPaymentInstrument',
    BILLING_ADDRESS_NOT_SET: 'billingAddressNotSet',
    PAYMENT_ADDRESS_MISMATCH: 'paymentAddressMismatch',
    UNSUPPORTED_PAYMENT_INSTRUMENT: 'unsupportedPaymentInstrument',
    INVALID_PAYMENT_INSTRUMENT: 'invalidPaymentInstrument',
};
export const DeliveryAddressErrorMessage = {
    DEFAULT_SHIPPING_ADDRESS_NOT_SET: 'defaultShippingAddressNotSet',
    CANT_SHIP_TO_ADDRESS: 'cannotShipToAddress',
};
export const getPreviewOrderState = (errorCode, isError) => {
    if (!!errorCode && PaymentMethodErrorMessage[errorCode]) {
        return {
            orderStatus: PreviewOrderStatus.FIXABLE_ERROR,
            errorType: PreviewOrderErrorType.PAYMENT_METHOD,
            translationKey: PaymentMethodErrorMessage[errorCode],
        };
    }
    else if (!!errorCode && DeliveryAddressErrorMessage[errorCode]) {
        return {
            orderStatus: PreviewOrderStatus.FIXABLE_ERROR,
            errorType: PreviewOrderErrorType.DELIVERY_ADDRESS,
            translationKey: DeliveryAddressErrorMessage[errorCode],
        };
    }
    else if (isError || !!errorCode) {
        return {
            orderStatus: PreviewOrderStatus.UNFIXABLE_ERROR,
            errorType: PreviewOrderErrorType.UNKNOWN,
            translationKey: 'couldNotBeProcessedViewOnAmazon',
        };
    }
    return {
        orderStatus: PreviewOrderStatus.SUCCESS,
        errorType: PreviewOrderErrorType.NONE,
        translationKey: 'previewAlert',
    };
};
